"use client";

import React, { useState, useEffect, Suspense, lazy, use } from 'react';
import classNames from 'classnames';
import { useIsLoading, useSearchTerm, useProductAllInfo, useRecommendationList, useProductBasicInfoList, useProductDetailInfoList, useProductReviewInfoList, useReview_summaryList, useVideoList, useBlogList, usePagination, useIsFilterCleaned, useProductAllInfoSource } from "@/app/context/StateProvider";
import Product from './Product';
import ReactGA from 'react-ga4';
import Rating from './Rating';
import { sortProducts, Sort, SortOption } from '../sort/SortAlgorithm';
import styles from "./productContainer.module.css";
import Image from 'next/image';
import { ShoppingItem, ProductVideos, ProductBlogs } from "../../../types/Product";
import { SpecsContainer } from '../productSpecs/SpecsContainer';
import { ProsAndCons } from '../prosAndCons/ProsAndCons';
const localhostAdress = process.env.NEXT_PUBLIC_LOCAL_HOST as string;
const remoteAddress = process.env.NEXT_PUBLIC_BACKEND_API as string;

/**
 * Set the host modes, it has 2 different modes:
 * 0 - localhost, the server will run locally
 * 1 - remote, the server will run remotely
 */
const hostMode: number = 1;
export const ProductContainer = ({
  productAllInfoList,
  productBasicInfoList,
  review_summaryList,
  productDetailInfoList,
  productReviewInfoList,
  tagList,
  videoList,
  blogList
}: {
  productAllInfoList: any[];
  productBasicInfoList: any[];
  review_summaryList: any[];
  productDetailInfoList: any[];
  productReviewInfoList: any[];
  tagList: any[];
  videoList: ProductVideos[][];
  blogList: ProductBlogs[][];
}) => {
  const [sortOption, setSortOption] = useState<SortOption>("default");
  const [activeTab, setActiveTab] = useState('Pros & Cons');
  const [expandedProductId, setExpandedProductId] = useState<number>(-1);
  const [isHighlightsExpanded, setIsHighlightsExpanded] = useState(true);
  const [recommendationList] = useRecommendationList();
  const [productAllInfo, setProductAllInfo] = useProductAllInfo();
  const [productAllInfoSource, setProductAllInfoSource] = useProductAllInfoSource();
  const [productBasicInfo, setProductBasicInfo] = useProductBasicInfoList();
  const [productDetailInfo, setProductDetailInfo] = useProductDetailInfoList();
  const [productReviewInfo, setProductReviewInfo] = useProductReviewInfoList();
  const [reviewSummaryInfo, setReviewSummaryInfo] = useReview_summaryList();
  const [videoListInfo, setVideoListInfo] = useVideoList();
  const [blogListInfo, setBlogListInfo] = useBlogList();
  const [pagination, setPagination] = usePagination();
  const [isFilterCleaned, setIsFilterCleaned] = useIsFilterCleaned();
  useEffect(() => {
    //console.log("enter useEffect in ProductContainer, productAllInfoList length", productAllInfoList.length);

    setProductAllInfoSource(productAllInfoList);
    setProductAllInfo(productAllInfoList);
    setProductBasicInfo(productBasicInfoList);
    setProductDetailInfo(productDetailInfoList);
    setProductReviewInfo(productReviewInfoList);
    setReviewSummaryInfo(review_summaryList);
    setVideoListInfo(videoList);
    setBlogListInfo(blogList);
    setIsFilterCleaned(false);
    setPagination(1);
  }, [productBasicInfoList, productDetailInfoList, productReviewInfoList, review_summaryList, videoList, blogList, isFilterCleaned]);
  useEffect(() => {
    //console.log("product order changed, pagination: productAllInfo length", pagination, productAllInfo.length);
    if (productAllInfo.length !== 0) {
      const newProductBasicInfo: any[] = [];
      const newProductDetailInfo: any[] = [];
      const newProductReviewInfo: any[] = [];
      productAllInfo.slice(0, 6 * pagination).forEach((productPack: any[]) => {
        newProductBasicInfo.push(productPack[0]);
        newProductDetailInfo.push(productPack[1]);
        newProductReviewInfo.push(productPack[2]);
      });
      setProductBasicInfo(newProductBasicInfo);
      setProductDetailInfo(newProductDetailInfo);
      setProductReviewInfo(newProductReviewInfo);
    } else {
      console.log("productAllInfoList is empty");
      setProductBasicInfo([]);
      setProductDetailInfo([]);
      setProductReviewInfo([]);
    }
  }, [pagination, productAllInfo]);
  const toggleHighlightsExpanded = () => {
    setIsHighlightsExpanded(!isHighlightsExpanded);
    if (isHighlightsExpanded) {
      ReactGA.event({
        category: 'Button Click',
        action: 'Expand highlights'
      });
    }
  };

  /**
  * This function changes the status of the item expansion. If the current item is not expanded, calling this function
  * will make the item expanded; if the current item is expanded, calling the function will make the item to be hidden.
  * @param {number} productId
  */
  const toggleExpansion = (index: number, action: string) => {
    if (expandedProductId === index) {
      switch (action) {
        case "click":
          setExpandedProductId(-1); // Collapse if it's already expanded
          break;
        case "hover":
          //Do nothing
          break;
      }
    } else {
      switch (action) {
        case "click":
          setExpandedProductId(index); // Expand the new one
          break;
        case "hover":
          if (expandedProductId === -1) {
            setExpandedProductId(index); // Expand the new one
          }
          break;
      }
    }
    ReactGA.event({
      category: 'Button Click',
      action: 'Expand item'
    });
  };
  const handleSort = (option: SortOption) => {
    sortProducts(option, productAllInfo, setProductAllInfo, setSortOption);
  };

  /**
  * This function is used to handle the click event of the recommendation card, it will open the link in a new tab
  * @param {string} link 
  */
  const handleClickRecommendationCard = (link: string) => {
    window.open(link, '_blank');
    ReactGA.event({
      category: 'Button Click',
      action: 'Open seller link'
    });
  };

  /**
  * This function get the product analysis result from the reviewResult useState
  * @param {string} productTitle 
  * @returns 
  */
  function getAnalysis(index: number) {
    //console.log("reviewResult: ", reviewResult);
    if (reviewSummaryInfo.length !== 0 && index < reviewSummaryInfo.length) {
      const result = reviewSummaryInfo[index];
      const convertedPositive = parseInt(result.positive, 10);
      const convertedNegative = parseInt(result.negative, 10);
      const convertedNeutral = parseInt(result.neutral, 10);
      const percentages = {
        positive_percentage: convertedPositive,
        critical_percentage: convertedNegative,
        neutral_percentage: convertedNeutral
      };
      //console.log("percentages: ", percentages);

      return percentages;
    } else {
      return {
        positive_percentage: 0,
        critical_percentage: 0,
        neutral_percentage: 0
      };
    }
  }

  /**
  * return the item expansion container
  * @param {*} param0 
  * @returns 
  */
  function MostMentionedopinionsContainer({
    index
  }: {
    index: number;
  }) {
    const result = productReviewInfo[index];
    type Highlight = [string, [number, number, number, number]];
    const displayedHighlights: Highlight[] = isHighlightsExpanded ? Object.entries(result) as Highlight[] : Object.entries(result).slice(0, 3) as Highlight[];

    // console.log('displayedHighlights',displayedHighlights);

    return <div id={styles.mostMentionedOpinionsContainer} onClick={toggleHighlightsExpanded} data-sentry-component="MostMentionedopinionsContainer" data-sentry-source-file="productContainer.tsx">
                <p className={styles.reviewHighlights}>Review Highlights</p>

                <div className={styles.reviewHighlightsContainer}>
                    {tagList.map(tag => {
          tag = tag.replace(/_/g, " ");
          if (tag !== "id" && tag !== "positive" && tag !== "negative" && tag !== "neutral" && result[tag]) {
            const score = result[tag][0];

            //Calculate the new 5-star score based on the sentiment percentage
            //const new_score = 4.5 * (positivePercentage/100) + (neutralPercentage/100) - 2*(negativePercentage/100) + 0.5;
            //const new_score = 2.5 + 2.5*(Math.min(2*positivePercentage - negativePercentage, 200))/200;
            //const formatted_score: number = Number(new_score.toFixed(1));

            return <div key={tag} className={styles.mostMentionedDataPoint}>
                                <div className={styles.dataPointTextGroup}>
                                    <p className={styles.dataPointContent}>{tag}</p>
                                </div>

                                <div className={styles.sentimentBarContainer}>
                                    <Rating score={score} />
                                </div>
                            </div>;
          } else {
            //console.log("This information will be not displayed: ", key);
            return null;
          }
        })}
                </div>

                <div className={styles.resourcesGroup}>
                    <p className={styles.resourcesText}>Insight sources from:</p>
                    <div className={styles.resourcesImgGroup}>
                        <Image className={styles.recommendedLogo} src="/resourcesIcon/amazon.png" alt="amazon" width={20} height={20} data-sentry-element="Image" data-sentry-source-file="productContainer.tsx" />
                        <Image className={styles.recommendedLogo} src="/resourcesIcon/google.png" alt="google" width={20} height={20} data-sentry-element="Image" data-sentry-source-file="productContainer.tsx" />
                        <Image className={styles.recommendedLogo} src="/resourcesIcon/reddit.png" alt="reddit" width={20} height={20} data-sentry-element="Image" data-sentry-source-file="productContainer.tsx" />
                        <Image className={styles.recommendedLogo} src="/resourcesIcon/ebay.png" alt="ebay" width={20} height={20} data-sentry-element="Image" data-sentry-source-file="productContainer.tsx" />

                    </div>

                </div>
            </div>;
  }

  /**
  * This function returns a container that contains all the recommendation products
  * @param {string} productTitle
  * @returns 
  */
  function RecommendationContainer({
    index
  }: {
    index: number;
  }) {
    //This useState controls the current index of the recommendation list
    const [currentIndex, setCurrentIndex] = useState({
      shopping: 0,
      media: 0
    });

    // This variable controls the number of products to be shown in the expansion
    const pageSize = 3;
    const shopping_result = productBasicInfo[index]?.recommendation_links;
    const productId = productBasicInfo[index]?.id;

    // Lazy load the MediaCards component
    const LazyMediaCards = lazy(() => import('./MediaCards'));

    /**
     * This function controls the scroller click event
     * If clicking the next button, the currentIndex will be increased by pageSize;
     * If clicking the previous button, the currentIndex will be decreased by pageSize;
     * @param {string} direction 
     * @param {string} type
     */
    const handleScrollerClick = (direction: string, type: 'shopping' | 'media') => {
      if (type === 'shopping') {
        let newIndex = currentIndex.shopping + (direction === 'next' ? pageSize : -pageSize);
        const maxIndex = shopping_result.length;
        if (newIndex >= maxIndex) newIndex = 0;else if (newIndex < 0) newIndex = Math.max(0, maxIndex - pageSize);
        setCurrentIndex(prevState => ({
          ...prevState,
          shopping: newIndex
        }));
      } else if (type === 'media') {
        let newIndex = currentIndex.media + (direction === 'next' ? 2 : -2);
        const maxIndex = blogListInfo[index].length + videoListInfo[index].length;
        if (newIndex >= maxIndex) newIndex = 0;else if (newIndex < 0) newIndex = Math.max(0, maxIndex - 1);
        setCurrentIndex(prevState => ({
          ...prevState,
          media: newIndex
        }));
      }
    };

    // Get the subset of products to display
    const visibleProducts = shopping_result?.slice(currentIndex.shopping, currentIndex.shopping + pageSize);
    return <div data-sentry-component="RecommendationContainer" data-sentry-source-file="productContainer.tsx">
                {/*<div id={styles.recommendationContainer}>
                    <p className={`${styles.recommendationTitle} pb-8`}>All sellers online:(Prices in ascending order)</p>
                    <div className={styles.recommendationTitleContainerHorizontal}>
                        <svg onClick={() => handleScrollerClick('prev', 'shopping')} id={styles.cardScrollButton} xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                            <path d="M10.7998 12.4998L14.6998 16.3998C14.8831 16.5831 14.9748 16.8165 14.9748 17.0998C14.9748 17.3831 14.8831 17.6165 14.6998 17.7998C14.5165 17.9831 14.2831 18.0748 13.9998 18.0748C13.7165 18.0748 13.4831 17.9831 13.2998 17.7998L8.6998 13.1998C8.5998 13.0998 8.52914 12.9915 8.4878 12.8748C8.44647 12.7581 8.42547 12.6331 8.4248 12.4998C8.4248 12.3665 8.4458 12.2415 8.4878 12.1248C8.5298 12.0081 8.60047 11.8998 8.6998 11.7998L13.2998 7.1998C13.4831 7.01647 13.7165 6.9248 13.9998 6.9248C14.2831 6.9248 14.5165 7.01647 14.6998 7.1998C14.8831 7.38314 14.9748 7.61647 14.9748 7.8998C14.9748 8.18314 14.8831 8.41647 14.6998 8.5998L10.7998 12.4998Z" fill="#212121" />
                            <path d="M10.7998 12.4998L14.6998 16.3998C14.8831 16.5831 14.9748 16.8165 14.9748 17.0998C14.9748 17.3831 14.8831 17.6165 14.6998 17.7998C14.5165 17.9831 14.2831 18.0748 13.9998 18.0748C13.7165 18.0748 13.4831 17.9831 13.2998 17.7998L8.6998 13.1998C8.5998 13.0998 8.52914 12.9915 8.4878 12.8748C8.44647 12.7581 8.42547 12.6331 8.4248 12.4998C8.4248 12.3665 8.4458 12.2415 8.4878 12.1248C8.5298 12.0081 8.60047 11.8998 8.6998 11.7998L13.2998 7.1998C13.4831 7.01647 13.7165 6.9248 13.9998 6.9248C14.2831 6.9248 14.5165 7.01647 14.6998 7.1998C14.8831 7.38314 14.9748 7.61647 14.9748 7.8998C14.9748 8.18314 14.8831 8.41647 14.6998 8.5998L10.7998 12.4998Z" fill="black" fillOpacity="0.2" />
                        </svg>
                         <div id={styles.recommendationListCards}>
                            {recommendationList && visibleProducts && visibleProducts.map((product: ShoppingItem, index: number) => {
                                return (
                                    <div id={styles.recommendedProductContainer} key={`${index}-${currentIndex}`} onClick={() => handleClickRecommendationCard(product.link)}>
                                         <p id={styles.cardPrice}>{product.price}</p>
                                        <p id={styles.cardSeller}>at {product.seller}</p>
                                         <div className={styles.recommendedProductDescription}>
                                            <div className={styles.recommendationDescriptionTextContainer} style={{ height: product.delivery ? "15px" : "0px" }}>
                                                <Image
                                                    className={styles.recommendationDescriptionCheck}
                                                    src={product.delivery === "Free delivery" ? "/assets/check3.png" : "/assets/dollarSign.png"}
                                                    alt="check"
                                                    width={8} // Specify width, adjust as needed
                                                    height={8} // Specify height, adjust as needed
                                                    style={{ display: product?.delivery ? "block" : "none" }}
                                                />
                                                <p className={styles.recommendationDescriptionText} >
                                                    {product.delivery}
                                                </p>
                                            </div>
                                             <div className={styles.recommendationDescriptionTextContainer} style={{ height: product.durability ? "12px" : "0px" }}>
                                                <Image
                                                    className={styles.recommendationDescriptionCheck}
                                                    src="/assets/usedSign.png"
                                                    alt="check"
                                                    width={8} // Specify width, adjust as needed
                                                    height={8} // Specify height, adjust as needed
                                                    style={{ display: product?.durability ? "block" : "none" }}
                                                />
                                                <p className={styles.recommendationDescriptionText} >
                                                    {product.durability}
                                                </p>
                                            </div>
                                             <div className={styles.recommendationDescriptionTextContainer} style={{ height: product.badge ? "15px" : "0px" }}>
                                                <Image
                                                    className={styles.recommendationDescriptionCheck}
                                                    src={product.badge === "No Return" ? "/assets/noReturn.png" : "/assets/check3.png"}
                                                    alt="check"
                                                    width={8} // Specify width, adjust as needed
                                                    height={8} // Specify height, adjust as needed
                                                    style={{ display: product?.badge ? "block" : "none" }}
                                                />
                                                <p className={styles.recommendationDescriptionText}>
                                                    {product.badge}
                                                </p>
                                            </div>
                                        </div>
                                        <div id={styles.recommendedProductButton}>
                                            <p id={styles.recommendedProductButtonText}>Visit Site</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                         <svg onClick={() => handleScrollerClick('next', 'shopping')} id={styles.cardScrollButton} xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                            <path d="M12.5998 12.4998L8.6998 8.5998C8.51647 8.41647 8.4248 8.18314 8.4248 7.8998C8.4248 7.61647 8.51647 7.38314 8.6998 7.1998C8.88314 7.01647 9.11647 6.9248 9.3998 6.9248C9.68314 6.9248 9.91647 7.01647 10.0998 7.1998L14.6998 11.7998C14.7998 11.8998 14.8708 12.0081 14.9128 12.1248C14.9548 12.2415 14.9755 12.3665 14.9748 12.4998C14.9748 12.6331 14.9538 12.7581 14.9118 12.8748C14.8698 12.9915 14.7991 13.0998 14.6998 13.1998L10.0998 17.7998C9.91647 17.9831 9.68314 18.0748 9.3998 18.0748C9.11647 18.0748 8.88314 17.9831 8.6998 17.7998C8.51647 17.6165 8.4248 17.3831 8.4248 17.0998C8.4248 16.8165 8.51647 16.5831 8.6998 16.3998L12.5998 12.4998Z" fill="#212121" />
                            <path d="M12.5998 12.4998L8.6998 8.5998C8.51647 8.41647 8.4248 8.18314 8.4248 7.8998C8.4248 7.61647 8.51647 7.38314 8.6998 7.1998C8.88314 7.01647 9.11647 6.9248 9.3998 6.9248C9.68314 6.9248 9.91647 7.01647 10.0998 7.1998L14.6998 11.7998C14.7998 11.8998 14.8708 12.0081 14.9128 12.1248C14.9548 12.2415 14.9755 12.3665 14.9748 12.4998C14.9748 12.6331 14.9538 12.7581 14.9118 12.8748C14.8698 12.9915 14.7991 13.0998 14.6998 13.1998L10.0998 17.7998C9.91647 17.9831 9.68314 18.0748 9.3998 18.0748C9.11647 18.0748 8.88314 17.9831 8.6998 17.7998C8.51647 17.6165 8.4248 17.3831 8.4248 17.0998C8.4248 16.8165 8.51647 16.5831 8.6998 16.3998L12.5998 12.4998Z" fill="black" fillOpacity="0.2" />
                        </svg>
                    </div>
                 </div>*/}

                <div className={styles.mediaContainer}>
                    <p className={styles.recommendationTitle}>Related Blogs and Videos</p>
                    <div className={styles.recommendationTitleContainerHorizontal}>
                        <svg onClick={() => handleScrollerClick('prev', 'media')} id={styles.cardScrollButton} xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" data-sentry-element="svg" data-sentry-source-file="productContainer.tsx">
                            <path d="M10.7998 12.4998L14.6998 16.3998C14.8831 16.5831 14.9748 16.8165 14.9748 17.0998C14.9748 17.3831 14.8831 17.6165 14.6998 17.7998C14.5165 17.9831 14.2831 18.0748 13.9998 18.0748C13.7165 18.0748 13.4831 17.9831 13.2998 17.7998L8.6998 13.1998C8.5998 13.0998 8.52914 12.9915 8.4878 12.8748C8.44647 12.7581 8.42547 12.6331 8.4248 12.4998C8.4248 12.3665 8.4458 12.2415 8.4878 12.1248C8.5298 12.0081 8.60047 11.8998 8.6998 11.7998L13.2998 7.1998C13.4831 7.01647 13.7165 6.9248 13.9998 6.9248C14.2831 6.9248 14.5165 7.01647 14.6998 7.1998C14.8831 7.38314 14.9748 7.61647 14.9748 7.8998C14.9748 8.18314 14.8831 8.41647 14.6998 8.5998L10.7998 12.4998Z" fill="#212121" data-sentry-element="path" data-sentry-source-file="productContainer.tsx" />
                            <path d="M10.7998 12.4998L14.6998 16.3998C14.8831 16.5831 14.9748 16.8165 14.9748 17.0998C14.9748 17.3831 14.8831 17.6165 14.6998 17.7998C14.5165 17.9831 14.2831 18.0748 13.9998 18.0748C13.7165 18.0748 13.4831 17.9831 13.2998 17.7998L8.6998 13.1998C8.5998 13.0998 8.52914 12.9915 8.4878 12.8748C8.44647 12.7581 8.42547 12.6331 8.4248 12.4998C8.4248 12.3665 8.4458 12.2415 8.4878 12.1248C8.5298 12.0081 8.60047 11.8998 8.6998 11.7998L13.2998 7.1998C13.4831 7.01647 13.7165 6.9248 13.9998 6.9248C14.2831 6.9248 14.5165 7.01647 14.6998 7.1998C14.8831 7.38314 14.9748 7.61647 14.9748 7.8998C14.9748 8.18314 14.8831 8.41647 14.6998 8.5998L10.7998 12.4998Z" fill="black" fillOpacity="0.2" data-sentry-element="path" data-sentry-source-file="productContainer.tsx" />
                        </svg>

                        <div className={styles.mediaListCards}>
                            <Suspense fallback={<div>Loading...</div>} data-sentry-element="Suspense" data-sentry-source-file="productContainer.tsx">
                                <LazyMediaCards productVideos={videoListInfo[index]} productBlogs={blogListInfo[index]} currentIndex={currentIndex} data-sentry-element="LazyMediaCards" data-sentry-source-file="productContainer.tsx" />
                            </Suspense>
                        </div>

                        <svg onClick={() => handleScrollerClick('next', 'media')} id={styles.cardScrollButton} xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" data-sentry-element="svg" data-sentry-source-file="productContainer.tsx">
                            <path d="M12.5998 12.4998L8.6998 8.5998C8.51647 8.41647 8.4248 8.18314 8.4248 7.8998C8.4248 7.61647 8.51647 7.38314 8.6998 7.1998C8.88314 7.01647 9.11647 6.9248 9.3998 6.9248C9.68314 6.9248 9.91647 7.01647 10.0998 7.1998L14.6998 11.7998C14.7998 11.8998 14.8708 12.0081 14.9128 12.1248C14.9548 12.2415 14.9755 12.3665 14.9748 12.4998C14.9748 12.6331 14.9538 12.7581 14.9118 12.8748C14.8698 12.9915 14.7991 13.0998 14.6998 13.1998L10.0998 17.7998C9.91647 17.9831 9.68314 18.0748 9.3998 18.0748C9.11647 18.0748 8.88314 17.9831 8.6998 17.7998C8.51647 17.6165 8.4248 17.3831 8.4248 17.0998C8.4248 16.8165 8.51647 16.5831 8.6998 16.3998L12.5998 12.4998Z" fill="#212121" data-sentry-element="path" data-sentry-source-file="productContainer.tsx" />
                            <path d="M12.5998 12.4998L8.6998 8.5998C8.51647 8.41647 8.4248 8.18314 8.4248 7.8998C8.4248 7.61647 8.51647 7.38314 8.6998 7.1998C8.88314 7.01647 9.11647 6.9248 9.3998 6.9248C9.68314 6.9248 9.91647 7.01647 10.0998 7.1998L14.6998 11.7998C14.7998 11.8998 14.8708 12.0081 14.9128 12.1248C14.9548 12.2415 14.9755 12.3665 14.9748 12.4998C14.9748 12.6331 14.9538 12.7581 14.9118 12.8748C14.8698 12.9915 14.7991 13.0998 14.6998 13.1998L10.0998 17.7998C9.91647 17.9831 9.68314 18.0748 9.3998 18.0748C9.11647 18.0748 8.88314 17.9831 8.6998 17.7998C8.51647 17.6165 8.4248 17.3831 8.4248 17.0998C8.4248 16.8165 8.51647 16.5831 8.6998 16.3998L12.5998 12.4998Z" fill="black" fillOpacity="0.2" data-sentry-element="path" data-sentry-source-file="productContainer.tsx" />
                        </svg>
                    </div>
                </div>
            </div>;
  }
  if (!productBasicInfo || productBasicInfo.length === 0) {
    return <></>;
  }

  // for each product in the list create a Product Component to display
  return <div className={styles.productContainer} data-sentry-component="ProductContainer" data-sentry-source-file="productContainer.tsx">
            
          {/*!isLoading && <Sort sortOption={sortOption} handleSort={handleSort} />*/}
  
          {productBasicInfo.map((product: any, index: number) => <div className={styles.homeRow} key={index}>
  
              <Product onExpandClick={() => toggleExpansion(index, "click")}
      // onMouseEnter={() => toggleExpansion(index, "hover")}
      {...product} analysis_result={getAnalysis(index)} index={index} expanded={expandedProductId === index} productBasicInfoList={productBasicInfo} productDetailInfoList={productDetailInfo} />
              
                <div className={classNames(styles.itemExpansion, {
        [styles.expanded]: expandedProductId === index
      })}>
                    <div className={styles.specsContainer}>
                        <div className="w-full pb-[3px] flex flex-row gap-5 pl-2 border-b-[color:var(--Foundation-Grey-grey-500,#9E9E9E)] border-b-solid border-b-[0.5px]">
                            <button className={activeTab === 'Pros & Cons' ? styles.activeTab : styles.unactiveTab} onClick={() => setActiveTab('Pros & Cons')}>
                                Pros & Cons
                            </button>
                            <button className={activeTab === 'Review Highlights' ? styles.activeTab : styles.unactiveTab} onClick={() => setActiveTab('Review Highlights')}>
                                Review Highlights
                            </button>
                            <button className={activeTab === 'Specs' ? styles.activeTab : styles.unactiveTab} onClick={() => setActiveTab('Specs')}>
                                Specs
                            </button>
                        </div>

                        {activeTab === 'Specs' && <div id={styles.productDetailContainerFooter}>
                                <SpecsContainer index={index} productDetailInfoList={productDetailInfo} />
                            </div>}

                        {activeTab === 'Pros & Cons' && <ProsAndCons index={index} productBasicInfoList={productBasicInfo} />}

                        {activeTab === 'Review Highlights' && <MostMentionedopinionsContainer index={index} />}
                    </div>

                    <div className={styles.mostMentionedWrapper}>
                        <MostMentionedopinionsContainer index={index} />
                    </div>

                    <RecommendationContainer index={index} />
                </div>

                    {expandedProductId === index && <div id={styles.upArrow} onClick={() => setExpandedProductId(-1)}>
                            <p>COLLAPSE</p>
                            <Image className={styles.arrowIcon} src="/assets/double-arrow-up-rounded.svg" alt="img" width={20} height={20}></Image>
                        </div>}

                </div>)}

        </div>;
};